import { Component } from '@theme/component';

/**
 * Photo Showcase component. Renders a grid of photos on desktop and a swipeable,
 * one-plus-peek carousel with arrow/dot controls on mobile, backed by a single scroll
 * container so no markup has to be duplicated between the two layouts.
 *
 * @typedef {object} PhotoShowcaseRefs
 * @property {HTMLElement} track - The scrollable container holding the photo items
 * @property {HTMLElement[]} [dots] - The dot navigation buttons
 * @property {HTMLButtonElement} [previous] - The previous arrow button
 * @property {HTMLButtonElement} [next] - The next arrow button
 *
 * @extends {Component<PhotoShowcaseRefs>}
 */
export class PhotoShowcaseComponent extends Component {
  requiredRefs = ['track'];

  connectedCallback() {
    super.connectedCallback();
    this.handleScroll = this.handleScroll.bind(this);
    this.refs.track.addEventListener('scroll', this.handleScroll, { passive: true });
    this.updateControls();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.track.removeEventListener('scroll', this.handleScroll);
    clearTimeout(this.scrollTimeout);
  }

  next() {
    this.scrollToIndex(this.getActiveIndex() + 1);
  }

  previous() {
    this.scrollToIndex(this.getActiveIndex() - 1);
  }

  /** @param {number} index */
  select(index) {
    this.scrollToIndex(Number(index));
  }

  getSlideWidth() {
    const track = this.refs.track;
    const firstSlide = track.children[0];

    if (!(firstSlide instanceof HTMLElement)) return track.clientWidth;

    const gap = parseFloat(getComputedStyle(track).columnGap || '0');
    return firstSlide.getBoundingClientRect().width + gap;
  }

  getActiveIndex() {
    const slideWidth = this.getSlideWidth();
    if (!slideWidth) return 0;

    return Math.round(this.refs.track.scrollLeft / slideWidth);
  }

  /** @param {number} index */
  scrollToIndex(index) {
    const track = this.refs.track;
    const maxIndex = track.children.length - 1;
    const clampedIndex = Math.max(0, Math.min(index, maxIndex));

    track.scrollTo({ left: clampedIndex * this.getSlideWidth(), behavior: 'smooth' });
  }

  handleScroll() {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => this.updateControls(), 100);
  }

  updateControls() {
    const activeIndex = this.getActiveIndex();
    const maxIndex = this.refs.track.children.length - 1;

    const dots = this.refs.dots ?? [];
    const dotsArray = Array.isArray(dots) ? dots : [dots];

    dotsArray.forEach((dot, index) => {
      dot.setAttribute('aria-selected', String(index === activeIndex));
    });

    if (this.refs.previous) this.refs.previous.disabled = activeIndex <= 0;
    if (this.refs.next) this.refs.next.disabled = activeIndex >= maxIndex;
  }
}

if (!customElements.get('photo-showcase-component')) {
  customElements.define('photo-showcase-component', PhotoShowcaseComponent);
}
